import {
  ImpactType,
  pluralise,
  ReferralImpact,
  User,
  UserType,
} from "@ecologi/shared";

type ImpactRecord<T> = Record<keyof typeof ImpactType, T>;

type ImpactCounts = ImpactRecord<number>;
type ImpactSummaryTexts = ImpactRecord<string>;

export function getProfileImpactCounts(
  {
    totalTrees,
    totalCarbonTonnes,
    totalCarbonRemovedTonnes,
    totalHabitatMetersRestored,
  }: User,
  referralData?: ReferralImpact
): ImpactCounts {
  const referralTrees =
    referralData && referralData.trees ? referralData.trees : [];

  const totalReferralTrees = referralTrees.reduce(
    (sum, t) => sum + t.totalTrees,
    0
  );

  return {
    [ImpactType.TREES]: totalTrees || totalReferralTrees,
    [ImpactType.CARBON_OFFSETS]: totalCarbonTonnes || 0,
    [ImpactType.CARBON_REMOVAL]: totalCarbonRemovedTonnes || 0,
    [ImpactType.HABITAT_RESTORATION]: totalHabitatMetersRestored || 0,
  };
}

export function getProfileShareMetadata(
  profile: User,
  referralData?: ReferralImpact
) {
  const baseText = `Check out ${profile.fullNamePlural} Ecologi profile:`;

  const impactCounts = getProfileImpactCounts(profile, referralData);
  const impactSummaryTexts = getImpactSummaryTexts(impactCounts);

  const validSummaryTexts = Object.entries(impactSummaryTexts)
    .filter(
      ([impactType, _]: [ImpactType, string]) => impactCounts[impactType] > 0
    )
    .map(([_, summary]) => summary);

  const summary = new Intl.ListFormat("en-GB").format(validSummaryTexts);

  return {
    shareTitle: profile.fullName(),
    shareDescription: `${baseText} ${summary}`,
  };
}

export const getFormattedUserType = (user: User | UserType): User => {
  /**
   * The profile will be of type `UserType` when viewing another users profile.
   *
   * If you are viewing your own profile (public or private) then we use the type from the store `User`
   *
   * The following logic converts `UserType` into `User`. The profile page UI is based off the `User` type.
   *
   * `User` is non-serialisable in Next SSR. Which is the main reason why there is two different type definitions.
   */
  const isUserType = (user as any)?.isUserType;
  return isUserType ? (user as User) : new User(user as UserType);
};

const getImpactSummaryTexts = (
  impactCounts: ImpactCounts
): ImpactSummaryTexts => {
  return {
    [ImpactType.TREES]: `${pluralise(impactCounts.TREES, "tree")} funded`,
    [ImpactType.CARBON_OFFSETS]: `${pluralise(
      impactCounts.CARBON_OFFSETS,
      "tonne"
    )} CO₂e avoided`,
    [ImpactType.CARBON_REMOVAL]: `${pluralise(
      impactCounts.CARBON_REMOVAL,
      "tonne"
    )} CO₂e removed`,
    [ImpactType.HABITAT_RESTORATION]: `${impactCounts.HABITAT_RESTORATION.toLocaleString()}m² habitat restored`,
  };
};
